/**
 * Last-mile polish on the matter pipeline's own sentences.
 *
 * The engine writes its step messages in English and sends them down the wire
 * verbatim, so the panel is the only place they can be fixed. Two kinds of
 * fix live here, and nothing else:
 *
 *  1. **Whole sentences that don't read.** `matter_processor` builds the
 *     retrieval line as `f"Retrieved {summary}."` where `summary` falls back
 *     to `"no sources found"` - so a run that found nothing said "Retrieved no
 *     sources found." Those are matched exactly and replaced with a translated
 *     line, which also gets that sentence into Bulgarian.
 *  2. **Typography.** The engine joins its counts with ` | ` and ends its
 *     in-flight lines with `...`; the app writes ` · ` and `…` everywhere
 *     else.
 *
 * A sentence this doesn't recognise passes through untouched - a copy change
 * on the engine side degrades to the engine's words, never to a wrong line.
 */

export type Translate = (key: string) => string;

/**
 * Engine sentences replaced whole. Keys are matched case-insensitively with
 * any trailing period ignored; values are i18n keys (en + bg).
 */
const SENTENCE_FIXES: Array<{ match: RegExp; key: string }> = [
  { match: /^retrieved no sources found$/i, key: 'No relevant sources found' },
  {
    match: /^task generation encountered an issue\.?\s*continuing$/i,
    key: 'Task suggestions could not be generated - continuing',
  },
];

/** ` | ` → ` · `, `...` → `…`, and no double spaces. */
function polishTypography(message: string): string {
  return message
    .replace(/\s*\|\s*/g, ' · ')
    .replace(/\.\.\.(\s*)$/, '…$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function polishMatterMessage(message: string, t: Translate): string {
  const trimmed = message.trim();
  if (!trimmed) return trimmed;

  const bare = trimmed.replace(/\.$/, '');
  const fix = SENTENCE_FIXES.find((f) => f.match.test(bare));
  if (fix) return t(fix.key);

  return polishTypography(trimmed);
}

export default polishMatterMessage;
